import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

type AnswerValueType = ArrayAnswerType[];

interface AnswerStateType {
  id: number;
  value: AnswerValueType;
}

export interface ArrayAnswerType {
  question: string;
  answer: string;
  questionIndex: number;
  answerIndex: number;
}

interface PillarState {
  id: number;
  answers: AnswerStateType[];
}

interface QuestionnaireState {
  pillarsStore: PillarState[];
  setPillarsStore: (pillarsStore: PillarState[]) => void;
  pillarsLengthStore: number;
  setPillarsLengthStore: (pillarsLengthStore: number) => void;
  setAnswerStore: (
    pillarId: number,
    answerId: number,
    answer: ArrayAnswerType[]
  ) => void;
  questionIndexStore: number;
  pillarIndexStore: number;
  setPillarIndexStore: (pillarIndex: number) => void;
  setQuestionIndexStore: (questionIndexStore: number) => void;
  displayResultsStore: boolean;
  setDisplayResultsStore: (displayResultsStore: boolean) => void;
  displayEndResultsStore: boolean;
  setDisplayEndResultsStore: (displayEndResultsStore: boolean) => void;
  firstName: string;
  setFirstName: (firstName: string) => void;
  lastName: string;
  setLastName: (lastName: string) => void;
  email: string;
  setEmail: (email: string) => void;
  company: string;
  setCompany: (company: string) => void;
  privacyPolicy: boolean;
  setPrivacyPolicy: (privacyPolicy: boolean) => void;
}

const updateAnswerInPillarStore = (
  pillar: PillarState,
  answerId: number,
  answer: ArrayAnswerType[]
): PillarState => {
  // Existing
  const answers = pillar?.answers ? [...pillar.answers] : [];

  // New
  answers[answerId] = {
    id: answerId + 1,
    value: answer,
  };

  return {
    ...pillar,
    answers: answers,
  };
};

const useQuestionnaireStore = create<QuestionnaireState>()(
  persist(
    (set) => ({
      pillarsStore: [],
      setPillarsStore: (pillarsStore) => set(() => ({ pillarsStore })),
      pillarsLengthStore: 0,
      setPillarsLengthStore: (pillarsLengthStore) =>
        set({ pillarsLengthStore }),
      displayResultsStore: false,
      setDisplayResultsStore: (displayResultsStore) =>
        set({ displayResultsStore }),
      displayEndResultsStore: false,
      setDisplayEndResultsStore: (displayEndResultsStore) =>
        set({ displayEndResultsStore }),
      pillarIndexStore: 0,
      setPillarIndexStore: (pillarIndexStore) => set({ pillarIndexStore }),
      questionIndexStore: 0,
      setQuestionIndexStore: (questionIndexStore) =>
        set({ questionIndexStore }),
      setAnswerStore: (pillarId, answerId, answer) => {
        set((state) => {
          // Existing
          const pillarsStore = [...state.pillarsStore];

          // Add Answer
          pillarsStore[pillarId] = updateAnswerInPillarStore(
            pillarsStore[pillarId],
            answerId,
            answer
          );

          // Add Id
          pillarsStore[pillarId] = {
            ...pillarsStore[pillarId],
            id: pillarId + 1,
          };

          // Return the new state with the updated pillars array
          return {
            ...state,
            pillarsStore,
          };
        });
      },
      firstName: '',
      setFirstName: (firstName) => set({ firstName }),
      lastName: '',
      setLastName: (lastName) => set({ lastName }),
      email: '',
      setEmail: (email) => set({ email }),
      company: '',
      setCompany: (company) => set({ company }),
      privacyPolicy: false,
      setPrivacyPolicy: (privacyPolicy) => set({ privacyPolicy }),
    }),
    {
      name: 'questionnaire-storage', // Name of the item in localStorage
      storage: createJSONStorage(() => localStorage), // Specify to use localStorage
    }
  )
);

export default useQuestionnaireStore;
